import { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, Smartphone, Sun, Moon } from "lucide-react";
import { useTheme } from "./theme-provider";
import { cn } from "@/lib/utils";

const GOLD = "#C9A867";
const basePath = import.meta.env.BASE_URL || "/docs/";
const LANDING_URL = import.meta.env.VITE_LANDING_URL || "https://nordex.trade";
const APP_URL = import.meta.env.VITE_FRONTEND_URL || "https://app.nordex.trade";
const APK_DOWNLOAD_URL =
  import.meta.env.VITE_APK_DOWNLOAD_URL ||
  "https://bcsnkehdffboctvgpwye.supabase.co/storage/v1/object/public/app-releases/android/nordex-android-v0.1.0-dev.apk";
const IOS_APP_URL =
  import.meta.env.VITE_IOS_APP_URL ||
  "https://testflight.apple.com/join/VnQeB4AZ";
const GOOGLE_PLAY_URL = import.meta.env.VITE_GOOGLE_PLAY_URL || "";

// -------------------------------------------------------------------
// App Download Popover
// -------------------------------------------------------------------
function AppDownloadPopover({ isDark }: { isDark: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDownload = (type: "ios" | "android" | "googleplay") => {
    if (type === "ios") {
      window.open(IOS_APP_URL, "_blank");
    } else if (type === "googleplay") {
      window.open(GOOGLE_PLAY_URL, "_blank");
    } else {
      window.location.href = APK_DOWNLOAD_URL;
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={popoverRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          "flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full transition-colors cursor-pointer",
          isDark ? "hover:bg-white/10" : "hover:bg-black/5"
        )}
        title="Download App"
      >
        <Smartphone
          className={cn(
            "w-4 h-4",
            isDark ? "text-white/80" : "text-foreground/70"
          )}
        />
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-56 sm:w-64 rounded-2xl border shadow-2xl overflow-hidden z-[60]"
          style={{
            background: isDark ? "rgba(13,13,15,0.95)" : "rgba(255,255,255,0.98)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            borderColor: isDark
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.08)",
          }}
        >
          <div
            className={cn(
              "px-4 py-2.5 border-b",
              isDark ? "border-white/10" : "border-black/5"
            )}
          >
            <p
              className={cn(
                "text-xs font-semibold",
                isDark ? "text-white/90" : "text-foreground/90"
              )}
            >
              Download App
            </p>
          </div>

          <button
            onClick={() => handleDownload("ios")}
            className={cn(
              "w-full flex items-center gap-3.5 px-4 py-3 transition-colors cursor-pointer",
              isDark ? "hover:bg-white/5" : "hover:bg-black/3"
            )}
          >
            <div className="w-9 h-9 rounded-lg bg-black flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 384 512" fill="white" className="h-5 w-5">
                <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p
                className={cn(
                  "text-sm font-semibold",
                  isDark ? "text-white" : "text-foreground"
                )}
              >
                App Store
              </p>
              <p
                className={cn(
                  "text-xs",
                  isDark ? "text-white/50" : "text-muted-foreground"
                )}
              >
                Download for iOS
              </p>
            </div>
          </button>

          <button
            onClick={() => handleDownload("android")}
            className={cn(
              "w-full flex items-center gap-3.5 px-4 py-3 transition-colors cursor-pointer",
              isDark ? "hover:bg-white/5" : "hover:bg-black/3"
            )}
          >
            <div className="w-9 h-9 rounded-lg bg-[#3DDC84] flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" fill="white" className="h-5 w-5">
                <path d="M17.6,9.48l1.84-3.18c0.16-0.31,0.04-0.69-0.26-0.85c-0.29-0.15-0.65-0.06-0.83,0.22l-1.88,3.24 c-2.86-1.21-6.08-1.21-8.94,0L5.65,5.67c-0.19-0.29-0.58-0.38-0.87-0.2C4.5,5.65,4.41,6.01,4.56,6.3L6.4,9.48 C3.3,11.25,1.28,14.44,1,18h22C22.72,14.44,20.7,11.25,17.6,9.48z M7,15.25c-0.69,0-1.25-0.56-1.25-1.25 c0-0.69,0.56-1.25,1.25-1.25S8.25,13.31,8.25,14C8.25,14.69,7.69,15.25,7,15.25z M17,15.25c-0.69,0-1.25-0.56-1.25-1.25 c0-0.69,0.56-1.25,1.25-1.25s1.25,0.56,1.25,1.25C18.25,14.69,17.69,15.25,17,15.25z" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <p
                className={cn(
                  "text-sm font-semibold",
                  isDark ? "text-white" : "text-foreground"
                )}
              >
                Android APK
              </p>
              <p
                className={cn(
                  "text-xs",
                  isDark ? "text-white/50" : "text-muted-foreground"
                )}
              >
                Direct Download
              </p>
            </div>
          </button>

          {GOOGLE_PLAY_URL && (
            <button
              onClick={() => handleDownload("googleplay")}
              className={cn(
                "w-full flex items-center gap-3.5 px-4 py-3 transition-colors cursor-pointer",
                isDark ? "hover:bg-white/5" : "hover:bg-black/3"
              )}
            >
              <div className="w-9 h-9 rounded-lg bg-black flex items-center justify-center flex-shrink-0">
                <svg viewBox="0 0 512 512" fill="white" className="h-5 w-5">
                  <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
                </svg>
              </div>
              <div className="flex-1 text-left">
                <p
                  className={cn(
                    "text-sm font-semibold",
                    isDark ? "text-white" : "text-foreground"
                  )}
                >
                  Google Play
                </p>
                <p
                  className={cn(
                    "text-xs",
                    isDark ? "text-white/50" : "text-muted-foreground"
                  )}
                >
                  Download for Android
                </p>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------
// Floating Header (Pill Menu)
// -------------------------------------------------------------------
export function FloatingHeader() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const isDark = theme === "dark";

  // Detect which context we're in
  const isSupport = window.location.pathname.startsWith("/support");

  // Show back arrow only on docs sub-pages (not on support pages or index)
  const isIndex =
    location.pathname === "/" ||
    location.pathname === "";
  const showBack = !isIndex && !isSupport;

  const handleBack = () => {
    // Try browser history, fallback to docs index
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  return (
    <header className="fixed top-4 left-1/2 -translate-x-1/2 z-50">
      {/* Frosted glass pill */}
      <div
        className={cn(
          "glass-pill flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-2.5 rounded-full border shadow-2xl transition-all duration-300",
          isDark ? "border-white/10" : "border-black/[0.06]"
        )}
      >
        {/* Back Arrow - appears on article pages */}
        <div
          className={cn(
            "flex items-center overflow-hidden transition-all duration-300 ease-out",
            showBack ? "w-8 opacity-100" : "w-0 opacity-0"
          )}
        >
          <button
            onClick={handleBack}
            className={cn(
              "flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full transition-colors flex-shrink-0 cursor-pointer",
              isDark ? "hover:bg-white/10" : "hover:bg-black/5"
            )}
            title="Go back"
          >
            <ArrowLeft
              className={cn(
                "w-4 h-4",
                isDark ? "text-white/80" : "text-foreground/70"
              )}
            />
          </button>
        </div>

        {/* Logo */}
        <a href={LANDING_URL} className="flex-shrink-0">
          {/* Desktop: Full logo */}
          <img
            src={
              isDark
                ? `${basePath}brand/Nordex_Logo_Color_White.svg`
                : `${basePath}brand/Nordex_Logo_Color_Black.svg`
            }
            alt="Nordex"
            className="hidden sm:block h-4"
          />
          {/* Mobile: N icon */}
          <img
            src={
              isDark
                ? `${basePath}brand/nordex_icon_white.svg`
                : `${basePath}brand/nordex_icon_black.svg`
            }
            alt="Nordex"
            className="sm:hidden h-6 w-6"
          />
        </a>

        {/* Divider */}
        <div
          className={cn(
            "w-px h-5",
            isDark ? "bg-white/20" : "bg-black/10"
          )}
        />

        {/* Trading Platform Button */}
        <a
          href={APP_URL}
          className="group flex items-center gap-1.5 text-xs font-semibold text-white px-3 sm:px-4 py-2 rounded-full transition-all hover:scale-105"
          style={{ background: GOLD }}
        >
          <span className="hidden sm:inline">Trading Platform</span>
          <span className="sm:hidden">Trade</span>
          <svg
            className="w-3 h-3 transition-transform duration-200 group-hover:translate-x-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </a>

        {/* App Download */}
        <AppDownloadPopover isDark={isDark} />

        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className={cn(
            "flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full transition-colors cursor-pointer",
            isDark ? "hover:bg-white/10" : "hover:bg-black/5"
          )}
          title={isDark ? "Switch to light mode" : "Switch to dark mode"}
        >
          {isDark ? (
            <Sun className="w-4 h-4 text-white/80" />
          ) : (
            <Moon className="w-4 h-4 text-foreground/70" />
          )}
        </button>
      </div>
    </header>
  );
}
